import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, User, Sprout, ArrowRight, Trash2, Camera, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getAdminHeaders, isFullAdmin } from '../lib/auth';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListaParticipantes = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [scores, setScores] = useState({});
  const [brokenPhotos, setBrokenPhotos] = useState({});

  useEffect(() => {
    fetchChildren();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredChildren(children);
    } else {
      const filtered = children.filter(child =>
        child.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredChildren(filtered);
    }
  }, [searchTerm, children]);

  const fetchChildren = async () => {
    try {
      const [childrenRes, scoresRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/scoring/all`)
      ]);
      setChildren(childrenRes.data);
      setFilteredChildren(childrenRes.data);
      
      const scoresMap = {};
      scoresRes.data.forEach(score => {
        scoresMap[score.child_id] = score.total_pontos;
      });
      setScores(scoresMap);
    } catch (error) {
      console.error('Erro ao carregar crianças:', error);
    }
  };

  const handleDelete = async (e, childId, childName) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/children/${childId}`, { headers: getAdminHeaders() });
      toast.success(`${childName} removido com sucesso`);
      fetchChildren();
    } catch (error) {
      toast.error('Erro ao excluir criança');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-6">
            <User className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Participantes</h1>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="search-input"
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 rounded-full bg-white border-none shadow-md focus:ring-2 focus:ring-white/50 text-[#1A4D2E]"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <p className="font-inter text-sm text-gray-500 mb-4">
          {filteredChildren.length} {filteredChildren.length === 1 ? 'criança encontrada' : 'crianças encontradas'}
        </p>

        {filteredChildren.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredChildren.map((child) => (
              <Card
                key={child.id}
                data-testid={`child-card-${child.id}`}
                onClick={() => navigate(`/participante/${child.id}`)}
                className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5] hover:shadow-xl hover:-translate-y-1 hover:border-[#1A4D2E]/30 cursor-pointer transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {child.numero > 0 && (
                        <span className="bg-[#1A4D2E] text-white text-xs font-bold px-2 py-0.5 rounded-full">#{child.numero}</span>
                      )}
                      <h3 className="font-nunito font-bold text-xl text-[#1A4D2E]">
                        {child.nome}
                      </h3>
                    </div>
                    <p className="font-inter text-sm text-gray-500">
                      {child.idade} {child.idade === 1 ? 'ano' : 'anos'}
                    </p>
                  </div>
                  {child.foto_url && !brokenPhotos[child.id] ? (
                    <img src={child.foto_url.startsWith('http') ? child.foto_url : `${BACKEND_URL}${child.foto_url}`} alt={child.nome} className="w-14 h-14 rounded-full object-cover border-2 border-[#E1ECE5] shrink-0"
                      onError={() => setBrokenPhotos(prev => ({ ...prev, [child.id]: true }))} />
                  ) : (
                    <div className="bg-[#F2F7F4] rounded-full p-3 shrink-0">
                      <User className="w-6 h-6 text-[#1A4D2E]" />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Sprout className="w-5 h-5 text-[#F4D35E]" />
                    <span className="font-nunito font-bold text-2xl text-[#1A4D2E]">
                      {scores[child.id] || 0}
                    </span>
                    <span className="font-inter text-sm text-gray-500">sementes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`btn-delete-${child.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-red-100 hover:bg-red-500 text-red-500 hover:text-white rounded-full p-2 transition-all"
                          title="Excluir criança"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir criança</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja excluir <strong>{child.nome}</strong>? Todas as atividades e pontuações serão removidas permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => handleDelete(e, child.id, child.nome)}
                            className="bg-red-500 hover:bg-red-600 text-white"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500 mb-4">
              {searchTerm ? 'Nenhuma criança encontrada' : 'Nenhuma criança cadastrada ainda'}
            </p>
            {!searchTerm && (
              <button
                data-testid="add-child-button"
                onClick={() => navigate('/cadastro')}
                className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-6 py-3 font-semibold shadow-md active:scale-95 transition-all"
              >
                Cadastrar Primeira Criança
              </button>
            )}
          </Card>
        )}
      </div>

      <Navigation active="participantes" />
    </div>
  );
};

export default ListaParticipantes;
